import React, { Fragment, useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import AssignCredit from '../components/manager/dashboard/assign-credit';
import NewsFeed from '../components/manager/dashboard/newsfeed';
import NewSubjects from '../components/manager/dashboard/newsubjects';
import { Row, Col } from 'reactstrap';
import ThemedSpinner from '../components/themed/ThemedSpinner';
import { Helmet } from 'react-helmet';
import { useStores } from '../hooks/use-stores';
import DashboardCalendar from '../components/manager/dashboard/calendar';
import AutomationDashboard from '../components/manager/dashboard/automation-dashboard';
import AssessmentDashboard from '../components/manager/dashboard/assessment-dashboard';
import AssessmentInsightDashboard from '../components/manager/dashboard/assessment-insight-dashboard';
import { FlexDiv } from '@awarego-ui/components';
import styled from 'styled-components';
import SubjectPreview from './subjectPreview';

const NarrowColumn = styled(Col)`
    max-width: 350px;
`;

function Home({ companyId }) {
    const { store, assessmentStore, assessmentInsightStore, automationStore, brandingStore } =
        useStores();

    const {
        serverData,
        currentCompany,
        loadingCalendarData,
        loadingDashboardData,
        dashboardData,
        daysWithEventsInSelectedMonth,
        trainingEnabled,
        assessmentEnabled,
        assessmentInsightEnabled,
        assessmentDashboardEnabled,
        assessmentsUnavailable,
        assessmentsUpgradeAllowed,
        trainingUpgradeAllowed,
        trainingProgramsUnavailable,
        deliveryCalendarDate,
        displayedCalendarEvents,
        latestSubjects,
        loadingLatestSubjects,
        cmi5enabled,
    } = store;

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [subjectModalIsOpen, setSubjectModalIsOpen] = useState(false);

    const doLoadDashboardData = useCallback(async () => {
        await store.loadDashboardData(companyId);
    }, [companyId, store]);

    const doLoadCalendarData = useCallback(async () => {
        await store.loadCalendarData(companyId);
    }, [store, companyId]);

    const doLoadLatestSubjects = useCallback(async () => {
        await store.loadLatestSubjects();
    }, [store]);

    const doLoadAutomations = useCallback(async () => {
        await automationStore.loadDashboardAutomations(companyId);
    }, [automationStore, companyId]);

    const doLoadAssessments = useCallback(async () => {
        await assessmentStore.loadDashboardAssessments(companyId);
    }, [assessmentStore, companyId]);

    const doLoadAssessmentInsights = useCallback(async () => {
        await assessmentInsightStore.loadDashboardAssessments(companyId);
    }, [assessmentInsightStore, companyId]);

    useEffect(() => {
        doLoadCalendarData();
    }, [doLoadCalendarData]);

    useEffect(() => {
        doLoadDashboardData();
        doLoadLatestSubjects();
        doLoadAutomations();
        doLoadAssessments();
        if (assessmentInsightEnabled) {
            doLoadAssessmentInsights();
        }
    }, [
        companyId,
        doLoadAssessments,
        doLoadAssessmentInsights,
        doLoadAutomations,
        doLoadDashboardData,
        doLoadLatestSubjects,
        assessmentInsightEnabled,
    ]);

    // Order dashboard sections so blocks with data come first (no empty block on top when another has data)
    const hasTrainings = automationStore.dashboardAutomations.length > 0;
    const hasAssessments = assessmentStore.dashboardAssessments.length > 0;
    const hasInsights = assessmentInsightStore.dashboardAssessments.length > 0;
    const dashboardSections = [
        trainingEnabled && {
            id: 'training',
            hasData: hasTrainings,
            order: 0,
            el: (
                <AutomationDashboard
                    key="training"
                    dashboardData={dashboardData.trainings}
                    trainings={[...automationStore.dashboardAutomations]}
                    trainingProgramsUnavailable={trainingProgramsUnavailable}
                    upgradeAllowed={trainingUpgradeAllowed}
                    supportEmail={brandingStore.supportEmail}
                    demoLink={automationStore.demoTrainingURL}
                    cmi5enabled={cmi5enabled}
                    loadingInBackground={
                        store.loadingDashboardDataInBackground ||
                        automationStore.loadingDashboardAutomationsInBackground
                    }
                />
            ),
        },
        assessmentEnabled &&
            assessmentDashboardEnabled && {
                id: 'assessments',
                hasData: hasAssessments,
                order: 1,
                el: (
                    <AssessmentDashboard
                        key="assessments"
                        dashboardData={dashboardData.assessments}
                        assessments={[...assessmentStore.dashboardAssessments]}
                        assessmentsUnavailable={assessmentsUnavailable}
                        upgradeAllowed={assessmentsUpgradeAllowed}
                        supportEmail={brandingStore.supportEmail}
                        demoLink={assessmentStore.demoAssessmentURL}
                        cmi5enabled={cmi5enabled}
                        isScormOnly={assessmentStore.isScormOnly}
                        loadingInBackground={
                            store.loadingDashboardDataInBackground ||
                            assessmentStore.loadingDashboardAssessmentsInBackground
                        }
                    />
                ),
            },
        assessmentInsightEnabled && {
            id: 'insights',
            hasData: hasInsights,
            order: 2,
            el: (
                <AssessmentInsightDashboard
                    key="insights"
                    dashboardData={dashboardData.assessmentInsights}
                    assessments={[
                        ...assessmentInsightStore.dashboardAssessments,
                    ]}
                    insightsLabel={brandingStore.insightsLabel}
                    insightsHeader={brandingStore.insightsHeaderTitle}
                    loadingInBackground={
                        store.loadingDashboardDataInBackground ||
                        assessmentInsightStore.loadingDashboardAssessmentsInBackground
                    }
                />
            ),
        },
    ]
        .filter(Boolean)
        .sort((a, b) =>
            (b.hasData - a.hasData) || (a.order - b.order)
        );

    const openSubjectModal = (subject) => {
        setSubjectModalIsOpen(true);
        setSelectedSubject(subject);
    };

    const closeSubjectModal = () => {
        setSubjectModalIsOpen(false);
    };

    const dashboardLoadingBlocking =
        loadingDashboardData ||
        automationStore.loadingDashboardAutomations ||
        assessmentStore.loadingDashboardAssessments ||
        assessmentInsightStore.loadingDashboardAssessments;

    return (
        <Fragment>
            <Helmet>
                <title>Dashboard</title>
            </Helmet>
            <Row>
                <Col lg="8" xs="12">
                    {serverData &&
                        serverData.unusedPackages &&
                        serverData.unusedPackages.length > 0 && (
                            <div>
                                {serverData.unusedPackages.map((pack, i) => (
                                    <AssignCredit
                                        key={i}
                                        unusedPackage={pack}
                                        companies={serverData.companies}
                                    />
                                ))}
                            </div>
                        )}
                    {dashboardLoadingBlocking ? (
                            <ThemedSpinner />
                        ) : (
                            <FlexDiv column>
                                {dashboardSections.map((s) => s.el)}
                            </FlexDiv>
                        )
                    }
                </Col>
                <NarrowColumn>
                    <DashboardCalendar
                        displayedEvents={displayedCalendarEvents}
                        onDayChange={store.setDeliveryCalendarDate}
                        setSelectedMonth={store.setDeliveryCalendarMonth}
                        selectedDate={deliveryCalendarDate}
                        daysWithDot={daysWithEventsInSelectedMonth}
                        isLoading={loadingCalendarData}
                    />
                    {trainingEnabled &&
                        (loadingLatestSubjects ? (
                            <ThemedSpinner />
                        ) : (
                            latestSubjects.length > 0 && (
                                <NewSubjects
                                    latestSubjects={latestSubjects}
                                    handleClick={openSubjectModal}
                                />
                            )
                        ))}
                    {!!currentCompany &&
                        currentCompany.awarego_company === 1 && <NewsFeed />}
                </NarrowColumn>
            </Row>

            {/* Subject modal*/}
            {selectedSubject && (
                <>
                    <SubjectPreview
                        subjectId={selectedSubject.subject_id}
                        isOpen={subjectModalIsOpen}
                        onClose={closeSubjectModal}
                        hideFooter={true}
                        showLanguageSelector={true}
                        availableLanguages={
                            selectedSubject.languages ||
                            currentCompany.defaultLanguage ||
                            []
                        }
                        defaultLanguage={currentCompany.defaultLanguage || 'en'}
                        subjectTitle={selectedSubject.title}
                    />
                </>
            )}
        </Fragment>
    );
}

export default observer(Home);
